/**
 * Cryptographic Role Verification
 *
 * Roles are cryptographically assigned by the organizer.
 * Only the room organizer (who has the original keypair) can assign roles.
 * Role assignments are signed to prevent spoofing.
 */

import { signMessage, verifySignature, uint8ArrayToBase64, base64ToUint8Array } from './identity'
import type { Role } from '@/lib/types'

/**
 * A cryptographically signed role assignment
 */
export interface RoleAssignment {
  /** Public key of the user being assigned a role (Base64) */
  assigneePublicKey: string
  /** The role being assigned */
  role: Role
  /** Timestamp of assignment */
  timestamp: number
  /** Room ID this assignment is for */
  roomId: string
  /** Public key of the assigner/organizer (Base64) */
  organizerPublicKey: string
  /** Signature over the assignment data (Base64) */
  signature: string
}

/**
 * Data structure for signing (without signature field)
 */
interface RoleAssignmentData {
  assigneePublicKey: string
  role: Role
  timestamp: number
  roomId: string
  organizerPublicKey: string
}

/**
 * Create signable content from role assignment data
 */
function getSignableContent(data: RoleAssignmentData): string {
  const signable = {
    assigneePublicKey: data.assigneePublicKey,
    role: data.role,
    timestamp: data.timestamp,
    roomId: data.roomId,
    organizerPublicKey: data.organizerPublicKey,
  }
  return JSON.stringify(signable, Object.keys(signable).sort())
}

/**
 * Create a signed role assignment (organizer only)
 */
export async function createRoleAssignment(
  assigneePublicKey: string,
  role: Role,
  roomId: string,
  organizerPrivateKey: Uint8Array,
  organizerPublicKey: Uint8Array
): Promise<RoleAssignment> {
  const organizerPubKeyBase64 = uint8ArrayToBase64(organizerPublicKey)

  const data: RoleAssignmentData = {
    assigneePublicKey,
    role,
    timestamp: Date.now(),
    roomId,
    organizerPublicKey: organizerPubKeyBase64,
  }

  // Sign the assignment
  const content = getSignableContent(data)
  const contentBytes = new TextEncoder().encode(content)
  const signature = await signMessage(contentBytes, organizerPrivateKey)

  return {
    ...data,
    signature: uint8ArrayToBase64(signature),
  }
}

/**
 * Verify a role assignment signature
 * Returns true if the assignment was signed by the organizer
 */
export async function verifyRoleAssignment(
  assignment: RoleAssignment
): Promise<boolean> {
  try {
    // Reconstruct signable content
    const data: RoleAssignmentData = {
      assigneePublicKey: assignment.assigneePublicKey,
      role: assignment.role,
      timestamp: assignment.timestamp,
      roomId: assignment.roomId,
      organizerPublicKey: assignment.organizerPublicKey,
    }

    const content = getSignableContent(data)
    const contentBytes = new TextEncoder().encode(content)

    // Decode signature and public key
    const signatureBytes = base64ToUint8Array(assignment.signature)
    const organizerPubKeyBytes = base64ToUint8Array(assignment.organizerPublicKey)

    // Verify signature
    const isValid = await verifySignature(signatureBytes, contentBytes, organizerPubKeyBytes)

    if (!isValid) {
      console.warn('[RoleVerification] Invalid role assignment signature')
    }

    return isValid
  } catch (error) {
    console.error('[RoleVerification] Verification error:', error)
    return false
  }
}

/**
 * Check if a role assignment is still valid (not too old)
 * Role assignments older than 24 hours should be considered stale
 */
export function isRoleAssignmentFresh(
  assignment: RoleAssignment,
  maxAgeMs: number = 24 * 60 * 60 * 1000 // 24 hours default
): boolean {
  const age = Date.now() - assignment.timestamp
  return age < maxAgeMs
}

/**
 * Verify that a role assignment is for the correct room
 */
export function isRoleAssignmentForRoom(
  assignment: RoleAssignment,
  roomId: string
): boolean {
  return assignment.roomId === roomId
}

/**
 * Get the verified role for a user based on their role assignment
 * Returns 'participant' if no valid assignment exists
 */
export async function getVerifiedRole(
  assignment: RoleAssignment | null | undefined,
  roomId: string,
  organizerPublicKey: string
): Promise<Role> {
  // No assignment means participant
  if (!assignment) {
    return 'participant'
  }

  // Check room match
  if (!isRoleAssignmentForRoom(assignment, roomId)) {
    console.warn('[RoleVerification] Assignment for wrong room')
    return 'participant'
  }

  // Check freshness (within 24 hours)
  if (!isRoleAssignmentFresh(assignment)) {
    console.warn('[RoleVerification] Stale role assignment')
    return 'participant'
  }

  // Verify the assignment came from the organizer
  if (assignment.organizerPublicKey !== organizerPublicKey) {
    console.warn('[RoleVerification] Assignment not from room organizer')
    return 'participant'
  }

  // Verify the signature
  const isValid = await verifyRoleAssignment(assignment)
  if (!isValid) {
    return 'participant'
  }

  return assignment.role
}

/**
 * Check if a user is the organizer based on their public key matching
 */
export function isOrganizerByKey(
  userPublicKey: string,
  organizerPublicKey: string
): boolean {
  return userPublicKey === organizerPublicKey
}
